import { EnvironmentProviders } from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  Routes,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig,
} from '@angular/router';

import { authGuard, authGuardChild } from '@core/auth';
import { Enums } from './app.enums';

import { LayoutComponent } from '@layout';
import { LoginComponent } from '@features/login/login.component';
import { LoginSamlComponent } from '@features/login-saml/login-saml.component';
import { LoginSamlCompleteComponent } from '@features/login-saml-complete/login-saml-complete.component';
import { AdminRegisterComponent } from '@features/admin-register/admin-register.component';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'login-saml',
    component: LoginSamlComponent,
  },
  {
    path: 'login-saml-complete',
    component: LoginSamlCompleteComponent,
  },
  {
    path: 'admin-register',
    component: AdminRegisterComponent,
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [authGuardChild],
    data: {
      title: 'Home',
      breadcrumb: 'Home',
      allowAnonymous: true,
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/dashboard/inbound',
      },
      {
        path: 'dashboard',
        data: {
          title: 'Dashboard',
          breadcrumb: 'Dashboard',
          roles: ['*'],
        },
        children: [
          {
            path: '',
            redirectTo: 'inbound',
            pathMatch: 'full',
          },
          {
            path: 'inbound',
            data: {
              title: 'Inbound Dashboard',
              breadcrumb: 'Inbound',
            },
            loadChildren: () => import('@features/dashboard-inbound/dashboard-inbound.routes').then((m) => m.routes),
          },
          {
            path: 'outbound',
            data: {
              title: 'Outbound Dashboard',
              breadcrumb: 'Outbound',
            },
            loadChildren: () => import('@features/dashboard-outbound/dashboard-outbound.routes').then((m) => m.routes),
          },
          {
            // Dynamic route for program-specific dashboards (e.g., /dashboard/connected, /dashboard/starlink)
            path: ':slug',
            data: {
              title: 'Program Dashboard',
              breadcrumb: 'Program Dashboard',
            },
            loadChildren: () => import('@features/dashboard-inbound/dashboard-inbound.routes').then((m) => m.routes),
          },
        ],
      },
      {
        path: 'leads',
        data: {
          title: 'Leads',
          breadcrumb: 'Leads',
          roles: [Enums.role.shiftAdmin, Enums.role.shiftAnalyst, Enums.role.corporate],
        },
        loadChildren: () => import('@features/leads/leads.routes').then((m) => m.routes),
      },
      {
        path: 'dispositions',
        data: {
          title: 'Dispositions',
          breadcrumb: 'Dispositions',
          roles: [Enums.role.shiftAdmin, Enums.role.shiftAnalyst, Enums.role.corporate],
        },
        loadChildren: () => import('@features/dispositions/dispositions.routes').then((m) => m.routes),
      },
      {
        path: 'users',
        data: {
          title: 'Users',
          breadcrumb: 'Users',
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () => import('@features/users/users.routes').then((m) => m.routes),
      },
      {
        path: 'retailers',
        data: {
          title: 'Retailers',
          breadcrumb: 'Retailers',
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () => import('@features/retailers/retailers.routes').then((m) => m.routes),
      },
      {
        path: 'programs',
        data: {
          title: 'Programs',
          breadcrumb: 'Programs',
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () => import('@features/programs/program.routes').then((m) => m.routes),
      },
      {
        path: 'manufacturers',
        data: {
          title: 'Manufacturers',
          breadcrumb: 'Manufacturers',
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () => import('@features/manufacturers/manufacturer.routes').then((m) => m.routes),
      },
      {
        path: 'brands',
        data: {
          title: 'Brands',
          breadcrumb: 'Brands',
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () => import('@features/brands/brand.routes').then((m) => m.routes),
      },
      {
        path: 'note-types',
        data: {
          title: 'Note Types',
          breadcrumb: 'Note Types',
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () => import('@features/note-types/note-type.routes').then((m) => m.routes),
      },
      {
        path: 'disposition-maps',
        data: {
          title: 'Disposition Maps',
          breadcrumb: 'Disposition Maps',
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () => import('@features/disposition-maps/disposition-map.routes').then((m) => m.routes),
      },
      {
        path: 'call-center-phone-number',
        data: {
          roles: [Enums.role.shiftAdmin],
        },
        loadChildren: () =>
          import('@features/call-center-phone-numbers/call-center-phone-number.routes').then((m) => m.routes),
      },
      {
        path: 'retailer-phone-numbers',
        data: {
          title: 'Retailer Phone Numbers',
          breadcrumb: 'Retailer Phone Numbers',
          roles: ['*'],
        },
        loadChildren: () =>
          import('@features/retailer-phone-numbers-search/retailer-phone-numbers-search.routes').then((m) => m.routes),
      },
    ],
  },
];

export const appRouterProviders: EnvironmentProviders = provideRouter(
  routes,
  withComponentInputBinding(),
  withPreloading(PreloadAllModules),
  withRouterConfig({
    onSameUrlNavigation: 'reload',
  })
);
